import { createSlice } from '@reduxjs/toolkit';
import { HttpStatusCode } from 'axios';
import {
  getUser,
  getOtp,
  verifyOtp,
  resendOtp,
  logoutOtp,
  updateUser,
  refreshTokenCall,
  updateUserPhoto,
} from '../thunk/auth-thunk';

const initialState = {
  loading: false,
  timer: 0,
  isLoggedIn: false,
  user: {},
  userError: '',
  isOpenLoginSignup: false,
  isOpenLoginSuccessModal: false,
  isLogoutPopupShow: false,
  isAccessDenied: false,
  OTPInfo: null,
  expiresAt: null,
  loginErrorMessage: '',
  currentPage: 'login',
  otpVerificationLocked: false,
  errorMessage: false, // for verify/ resend error state
  resendOTP: false,
  resending: false,
  isNumberOfAttemptExceeded: false,
  remainingAttempts: 0,
  isResendAttemptExceeded: false,
  accessToken: null,
  accessTokenExpiresAt: null,
  onLoginSuccess: () => null,
  userDataLoading: false,
  userPhotoDataLoading: false,
  userPhoto: '',
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setTimer(state, action) {
      state.timer = action.payload;
    },
    setUser(state, action) {
      state.user = action.payload;
    },
    setIsOpenLoginSignup(state, action) {
      state.isOpenLoginSignup = action.payload;
    },
    setIsOpenLoginSignupWithCallback(state, action) {
      state.isOpenLoginSignup = action.payload.state;
      state.onLoginSuccess = action.payload.onSuccessCallback;
    },
    setIsOpenLoginSuccessModal(state, action) {
      state.isOpenLoginSuccessModal = action.payload;
    },
    setIsLogoutPopupShow(state, action) {
      state.isLogoutPopupShow = action.payload;
    },
    setIsAccessDenied(state, action) {
      state.isAccessDenied = action.payload;
    },
    setCurrentPage(state, action) {
      state.currentPage = action.payload;
    },
    setLoginErrorMessage(state, action) {
      state.loginErrorMessage = action.payload;
    },
    setOtpVerificationLocked(state, action) {
      state.otpVerificationLocked = action.payload;
    },
    setRemoveProfilePhoto(state) {
      state.userPhoto = null;
    },
    reset(state) {
      state.loading = false;
      state.timer = 0;
      state.isLoggedIn = false;
      state.user = {};
      state.userError = '';
      state.isOpenLoginSuccessModal = false;
      state.isLogoutPopupShow = false;
      state.isAccessDenied = false;
      state.OTPInfo = null;
      state.expiresAt = null;
      state.loginErrorMessage = '';
      state.currentPage = 'login';
      state.otpVerificationLocked = false;
      state.errorMessage = false;
      state.resendOTP = false;
      state.resending = false;
      state.isNumberOfAttemptExceeded = false;
      state.remainingAttempts = 0;
      state.isResendAttemptExceeded = false;
      state.accessToken = null;
      state.accessTokenExpiresAt = null;
      state.userDataLoading = false;
      state.userPhotoDataLoading = false;
      state.userPhoto = '';
    },
    logout(state) {
      // reset state from thunk
      state.user = {};
      state.isLogoutPopupShow = true;
    },
    setBackButton(state) {
      // add case for back button
      state.isOpenLoginSignup = false;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getUser.pending, (state, action) => {
        state.user = {};
      })
      .addCase(getUser.fulfilled, (state, action) => {
        state.user = action.payload?.data || {};
        state.isLoggedIn = true;
      })
      .addCase(getUser.rejected, (state, action) => {
        state.user = {};
        state.userError = action.payload?.error || 'Failed to get user';
      })
      // UPDATE USER
      .addCase(updateUser.pending, (state, action) => {
        // add case for update user
        state.userDataLoading = true;
      })
      .addCase(updateUser.fulfilled, (state, action) => {
        state.user = action.payload?.data || {};
        state.userDataLoading = false;
      })
      .addCase(updateUser.rejected, (state, action) => {
        state.user = {};
        state.userError = action.payload?.error || 'Failed to update user';
        state.userDataLoading = false;
      })
      // UPDATE PHOTO
      .addCase(updateUserPhoto.pending, (state, action) => {
        // add case for update user
        state.userPhotoDataLoading = true;
        state.userPhoto = '';
      })
      .addCase(updateUserPhoto.fulfilled, (state, action) => {
        state.userPhoto = action.payload?.data;
        state.userPhotoDataLoading = false;
      })
      .addCase(updateUserPhoto.rejected, (state, action) => {
        state.userPhoto = '';
        state.userPhotoError =
          action.payload?.error || 'Failed to update user photo';
        state.userPhotoDataLoading = false;
      })
      // OTP LOGIN 1ST API
      .addCase(getOtp.pending, (state) => {
        state.loading = true;
        state.status = 'loading';
        state.error = null;
        state.timer = 0;
        state.isAccessDenied = false;
        state.loginErrorMessage = null;
      })
      .addCase(getOtp.fulfilled, (state, action) => {
        state.loading = false;
        state.status = 'succeeded';
        state.OTPInfo = { ...action.payload?.details };
        state.timer = action.payload?.details?.resendAfter;
        state.loginErrorMessage = null;
        state.currentPage = 'otp';
        state.isAccessDenied = false;
        state.otpVerificationLocked = false;
      })
      .addCase(getOtp.rejected, (state, action) => {
        // action.payload->{
        //     "status": "error",
        //     "code": 429,
        //     "data": null,
        //     "error": "Too many OTP requests for email. Please try again later."
        // }
        state.loading = false;
        state.status = action.payload.status;
        state.error = action.payload || 'Failed to send otp';
        state.timer = 0;
        state.loginErrorMessage = action.payload.error;
        state.otpVerificationLocked = true;

        if (action.payload.data?.error) {
          state.loginErrorMessage = action.payload.data?.error;
        }
        if (action.payload.data?.error?.errors?.[0]?.message) {
          state.loginErrorMessage =
            action.payload.data?.error?.errors?.[0]?.message;
        }
        if (
          action.payload.code === HttpStatusCode.Forbidden ||
          action.payload.code === HttpStatusCode.TooManyRequests
        ) {
          state.otpVerificationLocked = true;
          state.isAccessDenied = true;
          state.loginErrorMessage = '';
        }
      })
      // OTP VERIFY
      .addCase(verifyOtp.pending, (state) => {
        state.status = 'loading';
        state.loginErrorMessage = null;
        state.isAccessDenied = false;
        state.otpVerificationLocked = false;
        state.errorMessage = false;
      })
      .addCase(verifyOtp.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.loginErrorMessage = null;
        state.currentPage = 'successLogin';
        state.isLoggedIn = true;
        state.isOpenLoginSuccessModal = true;
        state.accessToken = action?.payload?.data?.token?.accessToken || null;
        state.accessTokenExpiresAt =
          action?.payload?.data?.token?.expiresAt || null;
        state.isOpenLoginSignup = false;
        state.timer = 0;
      })
      .addCase(verifyOtp.rejected, (state, action) => {
        state.isLoggedIn = false;
        state.status = action?.payload?.status;
        state.error = action?.payload || 'Failed to send otp';
        state.loginErrorMessage = action?.payload?.error;
        state.isAccessDenied = true;

        if (action?.payload?.data?.remainingAttempts <= 0) {
          state.otpVerificationLocked = true;
          state.isNumberOfAttemptExceeded = true;
        }
        state.errorMessage = 'wrong-otp';
        if (action?.payload?.data?.remainingAttempts === 1) {
          state.errorMessage = 'last-attempt';
        }
      })
      // OTP RESEND
      .addCase(resendOtp.pending, (state) => {
        state.status = 'loading';
        state.timer = 0;
        state.loginErrorMessage = null;
        state.isAccessDenied = false;
        state.resendOTP = true;
        state.resending = true;
        state.errorMessage = false;
      })
      .addCase(resendOtp.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.timer = action.payload?.resendAfter;
        state.loginErrorMessage = null;
        state.currentPage = 'otp';
        state.resending = false;
        state.remainingAttempts = action.payload?.remainingAttempts;
        if (action.payload?.remainingAttempts === 1) {
          state.errorMessage = 'last-resend-attempt';
        }
        if (action.payload?.remainingAttempts === 0) {
          state.errorMessage = '';
          state.isResendAttemptExceeded = true;
          state.timer = 0;
        }
      })
      .addCase(resendOtp.rejected, (state, action) => {
        state.status = action.payload?.status;
        state.error = action.payload || 'Failed to send otp';
        state.timer = 0;
        state.loginErrorMessage = action.payload.error;
        state.isAccessDenied = true;
        state.resendOTP = false;
        state.resending = false;
        // action.payload=>{
        //     "status": "error",
        //      "code": 400,
        //      "message": "OTP resent limit exceeded"
        // }
        if (action.payload.data?.remainingAttempts <= 0) {
          state.otpVerificationLocked = true;
          state.isNumberOfAttemptExceeded = true;
        }
        state.errorMessage = 'wrong-otp';
        if (action.payload.data?.remainingAttempts === 1) {
          state.errorMessage = 'last-attempt';
        }
      })
      // LOGOUT
      .addCase(logoutOtp.fulfilled, (state, action) => {
        // eslint-disable-next-line no-use-before-define
        state.loading = false;
        state.timer = 0;
        state.isLoggedIn = false;
        state.user = {};
        state.userError = '';
        state.isOpenLoginSuccessModal = false;
        state.isLogoutPopupShow = false;
        state.isAccessDenied = false;
        state.OTPInfo = null;
        state.expiresAt = null;
        state.loginErrorMessage = '';
        state.currentPage = 'login';
        state.otpVerificationLocked = false;
        state.errorMessage = false;
        state.resendOTP = false;
        state.resending = false;
        state.isNumberOfAttemptExceeded = false;
        state.remainingAttempts = 0;
        state.isResendAttemptExceeded = false;
        state.accessToken = null;
        state.accessTokenExpiresAt = null;
        state.userDataLoading = false;
      })
      // REFRESH TOKEN
      .addCase(refreshTokenCall.pending, (state, action) => {
        //
        state.isLoggedIn = true;
      })
      .addCase(refreshTokenCall.fulfilled, (state, action) => {
        //
        state.accessToken = action?.payload?.data?.token?.accessToken || null;
        state.accessTokenExpiresAt =
          action?.payload?.data?.token?.accessTokenExpiresAt || null;
        state.isLoggedIn = true;
      })
      .addCase(refreshTokenCall.rejected, (state, action) => {
        // call logout dispatch on refresh rejected
      });
  },
});

export const {
  setTimer,
  setUser,
  setIsOpenLoginSignup,
  setIsOpenLoginSignupWithCallback,
  setIsOpenLoginSuccessModal,
  setIsLogoutPopupShow,
  setIsAccessDenied,
  setCurrentPage,
  setLoginErrorMessage,
  setOtpVerificationLocked,
  reset,
  logout,
  setBackButton,
  setRemoveProfilePhoto,
} = authSlice.actions;

export default authSlice.reducer;
